// src/merch/removeBg.js
//
// On-device background removal for face photos (MediaPipe). The photo
// never leaves the device. Loaded lazily the first time someone adds a face.
//
//   removeBackground(bmp) → {
//     person: canvas  — photo with the background removed (selfie segmenter)
//     face:   canvas | null — just the face (landmark face oval, softened,
//                             ∩ person mask): no neck, hood or shoulders
//     info:   { cx, browY, chinY, width } | null — in photo pixels, for
//             auto-framing (works on iPhone, unlike FaceDetector)
//   }
// Canvases share the photo's aspect (max 1600 px) — draw them into the
// same rect as the bitmap.

const WASM = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.14/wasm";
// self-hosted copies first (public/merch/models/), Google's as fallback
const SEG_MODELS = [
  "/merch/models/selfie_segmenter.tflite",
  "https://storage.googleapis.com/mediapipe-models/image_segmenter/selfie_segmenter/float16/latest/selfie_segmenter.tflite",
];
const FACE_MODELS = [
  "/merch/models/face_landmarker.task",
  "https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task",
];
const MAX = 1600;

// face-mesh oval, in order around the face (forehead → right → chin → left)
const OVAL = [10, 338, 297, 332, 284, 251, 389, 356, 454, 323, 361, 288, 397, 365, 379, 378, 400, 377,
  152, 148, 176, 149, 150, 136, 172, 58, 132, 93, 234, 127, 162, 21, 54, 103, 67, 109];

let visionP = null;
const vision = () => {
  if (!visionP) {
    visionP = (async () => {
      const mod = await import("@mediapipe/tasks-vision");
      const fileset = await mod.FilesetResolver.forVisionTasks(WASM);
      return { mod, fileset };
    })();
    visionP.catch(() => { visionP = null; });
  }
  return visionP;
};

async function create(factory, fileset, models, opts) {
  let lastErr;
  for (const modelAssetPath of models) {
    for (const delegate of ["GPU", "CPU"]) {
      try { return await factory.createFromOptions(fileset, { baseOptions: { modelAssetPath, delegate }, ...opts }); }
      catch (e) { lastErr = e; }
    }
  }
  throw lastErr;
}

let segP = null, faceP = null;
function getSegmenter() {
  if (!segP) {
    segP = vision().then(({ mod, fileset }) => create(mod.ImageSegmenter, fileset, SEG_MODELS, {
      runningMode: "IMAGE", outputConfidenceMasks: true, outputCategoryMask: false,
    }));
    segP.catch(() => { segP = null; });
  }
  return segP;
}
function getLandmarker() {
  if (!faceP) {
    faceP = vision().then(({ mod, fileset }) => create(mod.FaceLandmarker, fileset, FACE_MODELS, {
      runningMode: "IMAGE", numFaces: 1,
    }));
    faceP.catch(() => { faceP = null; });
  }
  return faceP;
}

const canvas = (w, h) => { const c = document.createElement("canvas"); c.width = w; c.height = h; return c; };

/** Start downloading the models early (e.g. when the Face button shows),
 *  so the first photo doesn't wait for them. Safe to call many times. */
export function preloadFaceTools() {
  getSegmenter().catch(() => {});
  getLandmarker().catch(() => {});
}

/** onStage(name, progress 0–1): "loading" → "cutting" → "face" → "done". */
export async function removeBackground(bmp, onStage = () => {}) {
  onStage("loading", 0.1);
  const k = Math.min(1, MAX / Math.max(bmp.width, bmp.height));
  const w = Math.round(bmp.width * k), h = Math.round(bmp.height * k);
  const photo = canvas(w, h);
  photo.getContext("2d").drawImage(bmp, 0, 0, w, h);

  const [seg, lm] = await Promise.all([getSegmenter(), getLandmarker().catch(() => null)]);
  onStage("cutting", 0.55);
  await new Promise((r) => setTimeout(r, 0)); // let the progress paint before the heavy work

  // ---- person mask (confidence → soft alpha)
  const res = seg.segment(photo);
  const masks = res.confidenceMasks || [];
  const m = masks[masks.length - 1]; // selfie model: single "person" channel
  if (!m) throw new Error("no mask");
  const conf = m.getAsFloat32Array();
  const mask = canvas(m.width, m.height);
  const mctx = mask.getContext("2d");
  const md = mctx.createImageData(m.width, m.height);
  for (let i = 0; i < conf.length; i++) {
    let t = (conf[i] - 0.35) / 0.3;
    t = t < 0 ? 0 : t > 1 ? 1 : t;
    const j = i * 4;
    md.data[j] = md.data[j + 1] = md.data[j + 2] = 255;
    md.data[j + 3] = Math.round(t * t * (3 - 2 * t) * 255);
  }
  mctx.putImageData(md, 0, 0);
  masks.forEach((x) => x.close?.());

  const person = canvas(w, h);
  const pctx = person.getContext("2d");
  pctx.drawImage(photo, 0, 0);
  pctx.globalCompositeOperation = "destination-in";
  pctx.imageSmoothingQuality = "high";
  pctx.drawImage(mask, 0, 0, w, h);

  onStage("face", 0.8);
  await new Promise((r) => setTimeout(r, 0));
  // ---- face only (landmarks)
  let face = null, info = null;
  const lms = lm ? lm.detect(photo).faceLandmarks?.[0] : null;
  if (lms) {
    const P = (i) => [lms[i].x * w, lms[i].y * h];
    const oval = OVAL.map(P);
    const cx = oval.reduce((a, p) => a + p[0], 0) / oval.length;
    const cy = oval.reduce((a, p) => a + p[1], 0) / oval.length;
    // grow past the landmark outline so the soft edge fades OUTSIDE the
    // face, not into it: sideways for cheeks/ears, a lot more below so the
    // whole chin (and a beard) survives
    // the jaw tapers in toward the chin (cheeks keep their room, the
    // jawline hugs closer so less neck / collar comes along)
    const maxY = Math.max(...oval.map((p) => p[1]));
    const grown = oval.map(([x, y]) => {
      const t = y > cy ? Math.min(1, (y - cy) / Math.max(1, maxY - cy)) : 0;
      return [
        cx + (x - cx) * (1.12 - 0.07 * t),
        cy + (y - cy) * (y > cy ? 1.1 : 1.07),
      ];
    });
    // soft edge: draw the oval small, scale it up with smoothing
    const f = Math.max(4, Math.round(Math.max(w, h) / 260)); // ~6 px fade on a 1600 px photo: tight, keeps the chin
    const soft = canvas(Math.ceil(w / f), Math.ceil(h / f));
    const sctx = soft.getContext("2d");
    sctx.fillStyle = "#fff";
    sctx.beginPath();
    grown.forEach(([x, y], i) => (i ? sctx.lineTo(x / f, y / f) : sctx.moveTo(x / f, y / f)));
    sctx.closePath();
    sctx.fill();
    face = canvas(w, h);
    const fctx = face.getContext("2d");
    fctx.drawImage(person, 0, 0);
    fctx.globalCompositeOperation = "destination-in";
    fctx.imageSmoothingQuality = "high";
    fctx.drawImage(soft, 0, 0, w, h);

    const brow = (P(105)[1] + P(334)[1]) / 2;
    info = {
      cx: ((P(234)[0] + P(454)[0]) / 2) / k,
      browY: brow / k,
      chinY: P(152)[1] / k,
      width: Math.abs(P(454)[0] - P(234)[0]) / k,
    };
  }
  return { person, face, info };
}
